import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import imageCompression from 'browser-image-compression';
import { Plus, Loader2, ExternalLink, Save, ArrowLeft, Copy, Check, Camera } from 'lucide-react';
import { DashboardLayout } from '@/components/layout';
import { Card, CardTitle, Button, Input, Textarea, Avatar } from '@/components/ui';
import { AddLinkModal, LinkPageLinkItem, LinkPageStats } from '@/components/linkpage';
import { useLinkPage } from '@/hooks/useLinkPage';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/components/ui';
import { uploadFile, deleteFile } from '@/services/storage';
import { createDeeplink } from '@/services/deeplinks';
import { detectPlatform } from '@/utils/platformDetector';
import type { MusicMatchResult } from '@/types';

const LinkPageEditor = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { linkPage, loading, update, addLink, removeLink } = useLinkPage(id);
  const { user } = useAuth();
  const { addToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const headerInputRef = useRef<HTMLInputElement>(null);

  const [isAddLinkOpen, setIsAddLinkOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [isUploadingHeader, setIsUploadingHeader] = useState(false);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
  });

  useEffect(() => {
    if (linkPage) {
      setFormData({
        title: linkPage.title || '',
        description: linkPage.description || '',
      });
    }
  }, [linkPage]);

  const publicUrl = linkPage ? `https://klipr.it/p/${linkPage.code}` : '';

  const handleCopyUrl = async () => {
    if (!publicUrl) return;
    await navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !user) return;

    setIsUploadingPhoto(true);
    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 512,
        useWebWorker: true,
      });

      const ext = file.name.split('.').pop() || 'jpg';
      const path = `linkpages/${user.uid}/${Date.now()}.${ext}`;

      if (linkPage?.photoUrl?.includes('firebasestorage.googleapis.com')) {
        const oldPath = decodeURIComponent(
          linkPage.photoUrl.split('/o/')[1]?.split('?')[0] || ''
        );
        if (oldPath) await deleteFile(oldPath);
      }

      const downloadUrl = await uploadFile(path, compressed);
      await update({ photoUrl: downloadUrl });

      addToast({ type: 'success', title: t('linkPages.updated') });
    } catch {
      addToast({ type: 'error', title: t('common.somethingWentWrong') });
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleHeaderUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !user) return;

    setIsUploadingHeader(true);
    try {
      const compressed = await imageCompression(file, {
        maxSizeMB: 2,
        maxWidthOrHeight: 1200,
        useWebWorker: true,
      });

      const ext = file.name.split('.').pop() || 'jpg';
      const path = `linkpages/${user.uid}/header_${Date.now()}.${ext}`;

      if (linkPage?.headerImageUrl?.includes('firebasestorage.googleapis.com')) {
        const oldPath = decodeURIComponent(
          linkPage.headerImageUrl.split('/o/')[1]?.split('?')[0] || ''
        );
        if (oldPath) await deleteFile(oldPath);
      }

      const downloadUrl = await uploadFile(path, compressed);
      await update({ headerImageUrl: downloadUrl });

      addToast({ type: 'success', title: t('linkPages.updated') });
    } catch {
      addToast({ type: 'error', title: t('common.somethingWentWrong') });
    } finally {
      setIsUploadingHeader(false);
    }
  };

  const handleSave = async () => {
    if (!linkPage) return;
    setIsSaving(true);
    try {
      await update({
        title: formData.title,
        description: formData.description || undefined,
      });
      addToast({
        type: 'success',
        title: t('linkPages.updated'),
        message: t('linkPages.updatedMessage'),
      });
    } catch {
      addToast({ type: 'error', title: t('common.somethingWentWrong') });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddLink = async (data: {
    originalUrl: string;
    title?: string;
    musicMatches?: MusicMatchResult[];
  }) => {
    if (!user) throw new Error('Not authenticated');

    // Create the main deeplink
    const platform = detectPlatform(data.originalUrl);
    const deeplink = await createDeeplink({
      originalUrl: data.originalUrl,
      name: data.title,
      userId: user.uid,
    });

    const musicGroupId = data.musicMatches && data.musicMatches.length > 0
      ? `music_${Date.now()}`
      : undefined;

    // Add main link
    await addLink({
      deeplinkId: deeplink.id,
      deeplinkCode: deeplink.code,
      platform,
      originalUrl: data.originalUrl,
      title: data.title,
      musicMatchGroup: musicGroupId,
    });

    // Add music match links
    if (data.musicMatches) {
      for (const match of data.musicMatches) {
        try {
          const matchDeeplink = await createDeeplink({
            originalUrl: match.url,
            name: match.title || undefined,
            userId: user.uid,
          });
          await addLink({
            deeplinkId: matchDeeplink.id,
            deeplinkCode: matchDeeplink.code,
            platform: match.platform,
            originalUrl: match.url,
            title: match.title,
            musicMatchGroup: musicGroupId,
          });
        } catch (err) {
          console.error(`Failed to add match for ${match.platform}:`, err);
        }
      }
    }

    addToast({
      type: 'success',
      title: t('linkPages.linkAdded'),
      message: t('linkPages.linkAddedMessage'),
    });
  };

  const handleRemoveLink = async (linkId: string) => {
    try {
      await removeLink(linkId);
      addToast({
        type: 'success',
        title: t('linkPages.linkRemoved'),
        message: t('linkPages.linkRemovedMessage'),
      });
    } catch {
      addToast({ type: 'error', title: t('common.somethingWentWrong') });
    }
  };

  if (loading || !linkPage) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-lime-500" />
        </div>
      </DashboardLayout>
    );
  }

  const sortedLinks = [...(linkPage.links || [])].sort((a, b) => a.order - b.order);

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ArrowLeft className="w-4 h-4" />}
              onClick={() => navigate('/linkpages')}
            >
              {t('common.back')}
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-dark-800 dark:text-white">
                {t('linkPages.editLinkPage')}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href={publicUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="ghost" size="sm" leftIcon={<ExternalLink className="w-4 h-4" />}>
                {t('linkPages.preview')}
              </Button>
            </a>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Save className="w-4 h-4" />}
              onClick={handleSave}
              isLoading={isSaving}
            >
              {t('common.save')}
            </Button>
          </div>
        </div>

        {/* Public URL */}
        <Card variant="default" padding="md" className="mb-6">
          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-500 flex-shrink-0">{t('linkPages.publicUrl')}:</span>
            <code className="text-sm text-lime-600 dark:text-lime-400 font-mono truncate flex-1">
              {publicUrl}
            </code>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              onClick={handleCopyUrl}
            >
              {copied ? t('common.copied') : t('common.copyLink')}
            </Button>
          </div>
        </Card>

        {/* Page Settings */}
        <Card variant="default" padding="none" className="mb-6 overflow-hidden">
          {/* Header image area */}
          <div
            className="relative w-full h-40 bg-gradient-to-br from-gray-800 to-gray-900"
            style={linkPage.headerImageUrl ? {
              backgroundImage: `url(${linkPage.headerImageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            } : undefined}
          >
            <div className="absolute inset-0 bg-black/40" />
            <input
              type="file"
              ref={headerInputRef}
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={handleHeaderUpload}
            />
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-3 right-3 z-10 text-white bg-black/30 hover:bg-black/50 border-white/20"
              leftIcon={isUploadingHeader ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              onClick={() => headerInputRef.current?.click()}
              disabled={isUploadingHeader}
            >
              {t('linkPages.changeHeaderImage')}
            </Button>
          </div>

          {/* Avatar + form */}
          <div className="px-6 pb-6">
            <div className="flex items-start gap-6 -mt-8 relative z-10">
              <div className="flex-shrink-0">
                <div className="relative">
                  <Avatar
                    src={linkPage.photoUrl || undefined}
                    name={linkPage.title}
                    size="xl"
                    className="ring-4 ring-white dark:ring-gray-800"
                  />
                  {isUploadingPhoto && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full">
                      <Loader2 className="w-6 h-6 animate-spin text-white" />
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={handlePhotoUpload}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2 w-full"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingPhoto}
                  isLoading={isUploadingPhoto}
                >
                  {t('profile.change')}
                </Button>
              </div>

              <div className="flex-1 space-y-4 pt-10">
                <Input
                  label={t('linkPages.pageTitle')}
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder={t('linkPages.pageTitlePlaceholder')}
                />
                <Textarea
                  label={t('linkPages.description')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('linkPages.descriptionPlaceholder')}
                  rows={3}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Links */}
        <Card variant="default" padding="lg" className="mb-6">
          <div className="flex items-center justify-between mb-6">
            <CardTitle>
              {t('linkPages.links')} ({sortedLinks.length})
            </CardTitle>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => setIsAddLinkOpen(true)}
            >
              {t('linkPages.addLink')}
            </Button>
          </div>

          {sortedLinks.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 dark:text-gray-400 mb-2">
                {t('linkPages.noLinks')}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                {t('linkPages.noLinksDescription')}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {sortedLinks.map((link) => (
                <LinkPageLinkItem
                  key={link.id}
                  link={link}
                  onRemove={handleRemoveLink}
                />
              ))}
            </div>
          )}
        </Card>

        {/* Stats */}
        {id && <LinkPageStats linkpageId={id} />}
      </div>

      {/* Add Link Modal */}
      <AddLinkModal
        isOpen={isAddLinkOpen}
        onClose={() => setIsAddLinkOpen(false)}
        onSubmit={handleAddLink}
      />
    </DashboardLayout>
  );
};

export default LinkPageEditor;
